import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, getDocs, query, orderBy, deleteDoc, doc, where, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

// Component imports
import AppointmentCalendar from '../../components/AppointmentCalendar';
import PromotionalEmail from '../PromotionalEmail';
import MessageBox from '../../components/MessageBox';
import DailyAppointmentsList from '../../components/DailyAppointmentList';
import MobileSidebarControl from './MobileSidebarControl';

// Style imports
import '../../components/styles/admin.css';
import '../../components/styles/calendar.css';
import '../../components/styles/MessageBox.css';
import '../../components/styles/promotionalEmail.css';
import '../../components/styles/MobileSidebarControl.css';

// Icon imports
import { 
  Calendar, 
  MessageSquare, 
  Clock, 
  LogOut, 
  User, 
  Check, 
  X, 
  Trash2, 
  Mail, 
  Calendar as CalendarIcon, 
  Clock as ClockIcon, 
  Info, 
  Menu, 
  Phone,
  List,
  Send,
  Search, 
  Filter    
} from 'react-feather';

function Admin() {
  // State management
  const [currentView, setCurrentView] = useState('login');
  const [activeTab, setActiveTab] = useState('appointments');
  const [appointmentView, setAppointmentView] = useState('calendar');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data state
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  
  // Form state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [newTimeBlock, setNewTimeBlock] = useState({
    date: '',
    startTime: '',
    endTime: '',
    reason: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [messageFilter, setMessageFilter] = useState('all'); // 'all', 'unread', 'replied'
  
  // Then add this derived state before the return statement
  // Filter and search messages
  const filteredMessages = messages.filter(message => {
    // Apply status filter
    if (messageFilter !== 'all' && message.status !== messageFilter) {
      return false;
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = `${message.firstName} ${message.lastName}`.toLowerCase().includes(searchLower);
      const emailMatch = message.email.toLowerCase().includes(searchLower);
      const messageMatch = message.message.toLowerCase().includes(searchLower);
      
      return nameMatch || emailMatch || messageMatch;
    }
    
    return true;
  });
  const navigate = useNavigate();
  const auth = getAuth();

  // Set sidebar to be open by default on desktop, closed on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    // Set initial state based on window width
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Authentication effect
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const adminSnapshot = await getDocs(
          query(collection(db, 'admins'), where('userId', '==', user.uid))
        );
        
        if (!adminSnapshot.empty) {
          setCurrentView('dashboard');
          fetchDashboardData();
        } else {
          handleLogout();
          setError('Not authorized as admin');
        }
      } else {
        setCurrentView('login');
      }
    });

    return () => unsubscribe();
  }, []);

  // Data fetching functions
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [appointmentsData, messagesData, blockedData] = await Promise.all([
        fetchAppointments(),
        fetchMessages(),
        fetchBlockedTimes()
      ]);
      
      setAppointments(appointmentsData);
      setMessages(messagesData);
      setBlockedTimes(blockedData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchAppointments = async () => {
    const snapshot = await getDocs(
      query(collection(db, 'bookings'), orderBy('date', 'desc'))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const fetchMessages = async () => {
    const snapshot = await getDocs(
      query(collection(db, 'messages'), orderBy('createdAt', 'desc'))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const fetchBlockedTimes = async () => {
    const snapshot = await getDocs(
      query(collection(db, 'blockedTimes'), orderBy('date', 'asc'))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Action handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
    } catch (error) {
      setError('Invalid login credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentView('login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAppointmentStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, 'bookings', id), {
        status,
        updatedAt: new Date().toISOString()
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await deleteDoc(doc(db, 'bookings', id));
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting appointment:', error);
      }
    }
  };

  const handleMessageReply = async (messageId, replyText) => {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        reply: replyText,
        status: 'replied',
        repliedAt: new Date().toISOString()
      });
      
      // Update the message in the local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { 
                ...msg, 
                reply: replyText, 
                status: 'replied', 
                repliedAt: new Date().toISOString() 
              } 
            : msg
        )
      );
      
      return true;
    } catch (error) {
      console.error('Error replying to message:', error);
      return false;
    }
  };

  const handleBlockTime = async (blockData) => {
    try {
      // Use passed blockData if provided, otherwise use state
      const dataToUse = blockData || newTimeBlock;
      
      await addDoc(collection(db, 'blockedTimes'), {
        ...dataToUse,
        createdAt: new Date().toISOString()
      });
      
      // Reset form state if using internal state
      if (!blockData) {
        setNewTimeBlock({ date: '', startTime: '', endTime: '', reason: '' });
      }
      
      fetchDashboardData();
    } catch (error) {
      console.error('Error blocking time:', error);
    }
  };

  const handleUnblockTime = async (id) => {
    if (window.confirm('Are you sure you want to remove this blocked time?')) {
      try {
        await deleteDoc(doc(db, 'blockedTimes', id));
        fetchDashboardData();
      } catch (error) {
        console.error('Error removing blocked time:', error);
      }
    }
  };

  // Close sidebar after navigation on mobile
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // Render login view
  if (currentView === 'login') {
    return (
      <div className="admin-login">
        <div className="login-container">
          <div className="login-header">
            <h1>Admin Login</h1>
            <p>Welcome back! Please log in to continue.</p>
          </div>
          
          {error && <div className="error-alert">{error}</div>}
          
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
              />
            </div>
            
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render dashboard view
  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="sidebar-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <button
            className={activeTab === 'appointments' ? 'active' : ''}
            onClick={() => handleTabChange('appointments')}
          >
            <Calendar size={18} />
            <span>Appointments</span>
          </button>
          
          <button
            className={activeTab === 'messages' ? 'active' : ''}
            onClick={() => handleTabChange('messages')}
          >
            <MessageSquare size={18} />
            <span>Messages</span>
            {messages.filter(m => m.status === 'unread' || !m.status).length > 0 && (
              <span className="badge">{messages.filter(m => m.status === 'unread' || !m.status).length}</span>
            )}
          </button>
          
          <button
            className={activeTab === 'promotions' ? 'active' : ''}
            onClick={() => handleTabChange('promotions')}
          >
            <Send size={18} />
            <span>Promotions</span>
          </button>
          
          <button
            className={activeTab === 'schedule' ? 'active' : ''}
            onClick={() => handleTabChange('schedule')}
          >
            <Clock size={18} />
            <span>Schedule</span>
          </button>
        </nav>
        
        <button className="logout-button" onClick={handleLogout}>
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>

      {/* Mobile sidebar controls */}
      <MobileSidebarControl 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main content */}
      <main className="dashboard-content">
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : (
          <>
            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div className="appointments-view">
                <div className="view-header">
                  <h2>Appointments</h2>
                  <div className="view-toggle">
                    <button 
                      className={`toggle-button ${appointmentView === 'calendar' ? 'active' : ''}`}
                      onClick={() => setAppointmentView('calendar')}
                    >
                      <Calendar size={16} /> Calendar
                    </button>
                    <button 
                      className={`toggle-button ${appointmentView === 'list' ? 'active' : ''}`}
                      onClick={() => setAppointmentView('list')}
                    >
                      <List size={16} /> List
                    </button>
                  </div>
                </div>

                {appointments.length === 0 ? (
                  <div className="empty-state">
                    <Calendar size={48} />
                    <p>No appointments scheduled</p>
                  </div>
                ) : (
                  <>
                    {appointmentView === 'calendar' ? (
                      // Calendar view
                      <AppointmentCalendar 
                        appointments={appointments} 
                        onStatusChange={handleAppointmentStatus}
                        onDelete={handleDeleteAppointment}
                      />
                    ) : (
                      // List view
                      <div className="appointments-grid">
                        {appointments.map(appointment => (
                          <div key={appointment.id} className="appointment-card">
                            <div className="appointment-header">
                              <h3>{appointment.name}</h3>
                              <span className={`status-badge ${appointment.status || 'pending'}`}>
                                {appointment.status || 'pending'}
                              </span>
                            </div>

                            <div className="date-time-section">
                              <p><CalendarIcon size={16} /> {appointment.date}</p>
                              <p><ClockIcon size={16} /> {appointment.time}</p>
                            </div>

                            <div className="contact-info">
                              <h4>Contact Information</h4>
                              <p><Mail size={16} /> {appointment.email}</p>
                              <p><Phone size={16} /> {appointment.phone}</p>
                            </div>

                            <div className="service-info">
                              <p><Info size={16} /> {appointment.service}</p>
                            </div>

                            <div className="appointment-actions">
                              <button
                                onClick={() => handleAppointmentStatus(appointment.id, 'confirmed')}
                                className="confirm-button"
                              >
                                <Check size={16} /> Confirm
                              </button>
                              <button
                                onClick={() => handleAppointmentStatus(appointment.id, 'cancelled')}
                                className="cancel-button"
                              >
                                <X size={16} /> Cancel
                              </button>
                              <button
                                onClick={() => handleDeleteAppointment(appointment.id)}
                                className="delete-button"
                              >
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="messages-view">
                <div className="messages-header">
                  <h2><MessageSquare size={20} /> Messages</h2>
                  
                  <div className="messages-controls">
                    <div className="search-box">
                      <Search size={18} />
                      <input 
                        type="text" 
                        placeholder="Search messages..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    
                    <div className="filter-controls">
                      <Filter size={18} />
                      <select 
                        value={messageFilter} 
                        onChange={(e) => setMessageFilter(e.target.value)}
                      >
                        <option value="all">All Messages</option>
                        <option value="unread">Unread</option>
                        <option value="replied">Replied</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {filteredMessages.length === 0 ? (
                  <div className="empty-state">
                    <MessageSquare size={48} />
                    <p>No messages found</p>
                    {searchTerm && <p>Try changing your search criteria</p>}
                  </div>
                ) : (
                  <div className="messages-list">
                    {filteredMessages.map(message => (
                      <MessageBox 
                        key={message.id} 
                        message={message} 
                        onReply={handleMessageReply}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Promotions Tab */}
            {activeTab === 'promotions' && (
              <PromotionalEmail />
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && (
              <div className="schedule-view">
                <h2>Schedule Management</h2>
                <div className="schedule-container">
                  <div className="block-time-form">
                    <h3>Block Time Slot</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          value={newTimeBlock.date}
                          onChange={(e) => setNewTimeBlock({
                            ...newTimeBlock,
                            date: e.target.value
                          })}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Start Time</label>
                        <input
                          type="time"
                          value={newTimeBlock.startTime}
                          onChange={(e) => setNewTimeBlock({
                            ...newTimeBlock,
                            startTime: e.target.value
                          })}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Time</label>
                        <input
                          type="time"
                          value={newTimeBlock.endTime}
                          onChange={(e) => setNewTimeBlock({
                            ...newTimeBlock,
                            endTime: e.target.value
                          })}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Reason</label>
                        <input
                          type="text"
                          value={newTimeBlock.reason}
                          onChange={(e) => setNewTimeBlock({
                            ...newTimeBlock,
                            reason: e.target.value
                          })}
                          placeholder="Enter reason for blocking"
                        />
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => handleBlockTime()}
                      className="block-button"
                      disabled={!newTimeBlock.date || !newTimeBlock.startTime || !newTimeBlock.endTime}
                    >
                      Block Time Slot
                    </button>
                  </div>

                  <div className="blocked-times-list">
                    <h3>Blocked Time Slots</h3>
                    {blockedTimes.length === 0 ? (
                      <div className="empty-state">
                        <Clock size={48} />
                        <p>No blocked time slots</p>
                      </div>
                    ) : (
                      <div className="blocked-times-grid">
                        {blockedTimes.map(block => (
                          <div key={block.id} className="blocked-time-card">
                            <div className="blocked-time-header">
                              <h4>{block.date}</h4>
                              <button
                                onClick={() => handleUnblockTime(block.id)}
                                className="unblock-button"
                                aria-label="Remove blocked time"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            
                            <div className="blocked-time-details">
                              <p>
                                <ClockIcon size={14} />
                                {block.startTime} - {block.endTime}
                              </p>
                              <p>
                                <Info size={14} />
                                {block.reason}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default Admin;